import type { ChimeField, ChimeSiren, ChimeValues } from "@/lib/api/doorbell-chime";

/** The length when none is set (the backend's `doorbell.chime.DEFAULT_SECONDS`, also the field's `default`). */
export const DEFAULT_CHIME_SECONDS = 3;
/** The backend allows one test play per siren in this window (`PLAY_SECONDS`). */
export const PLAY_COOLDOWN_MS = 10_000;

export const CHIME_KEYS = ["doorbell_chime", "doorbell_sound", "doorbell_volume", "doorbell_seconds"] as const;

/** The values to send: unset keys dropped (an empty sound = the siren's own). */
export function chimePayload(values: ChimeValues): ChimeValues {
  const out: ChimeValues = {};
  if (typeof values.doorbell_chime === "boolean") out.doorbell_chime = values.doorbell_chime;
  if (values.doorbell_sound) out.doorbell_sound = values.doorbell_sound;
  if (values.doorbell_volume) out.doorbell_volume = values.doorbell_volume;
  if (typeof values.doorbell_seconds === "number" && Number.isFinite(values.doorbell_seconds)) {
    out.doorbell_seconds = values.doorbell_seconds;
  }
  return out;
}

/** Whether the draft differs from what is saved. */
export function chimeChanged(saved: ChimeValues, draft: ChimeValues): boolean {
  const a = chimePayload(saved);
  const b = chimePayload(draft);
  return CHIME_KEYS.some((k) => (a[k] ?? null) !== (b[k] ?? null));
}

export function fieldOf(fields: ChimeField[], key: ChimeField["key"]): ChimeField | undefined {
  return fields.find((f) => f.key === key);
}

/** A stable id for one siren (React keys, the playing/saving state). */
export function sirenKey(s: Pick<ChimeSiren, "provider" | "source_id">): string {
  return `${s.provider}:${s.source_id}`;
}

/** The backend's message, or a plain fallback. */
export function chimeError(err: unknown, fallback = "Something went wrong. Please try again."): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
}
